import jsPDF from 'jspdf';
import apiV2 from '@/api/v2';
import { getFullAddress } from '@/util/common';
import { getObjectDimensions } from './dimensions';
function blobToDataUrl(blob) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
    });
}
async function fetchFileViaApi(fileId) {
    try {
        const response = await apiV2.get(`/files/${fileId}`, {
            responseType: 'blob',
        });
        return await blobToDataUrl(response.data);
    }
    catch {
        return null;
    }
}
async function loadImageAsBase64(url) {
    try {
        const response = await fetch(url);
        if (!response.ok)
            return null;
        const blob = await response.blob();
        return await blobToDataUrl(blob);
    }
    catch {
        return null;
    }
}
function buildStaticMapUrl(lat, lng, apiKey) {
    const params = new URLSearchParams({
        center: `${lat},${lng}`,
        zoom: '15',
        size: '600x400',
        scale: '2',
        markers: `color:red|${lat},${lng}`,
        key: apiKey,
    });
    return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}
function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
async function fetchFontAsBase64(path) {
    const response = await fetch(path);
    const buffer = await response.arrayBuffer();
    return arrayBufferToBase64(buffer);
}
async function registerFonts(doc) {
    try {
        const [regularBase64, boldBase64] = await Promise.all([
            fetchFontAsBase64('/fonts/Roboto-Regular.ttf'),
            fetchFontAsBase64('/fonts/Roboto-Bold.ttf'),
        ]);
        doc.addFileToVFS('Roboto-Regular.ttf', regularBase64);
        doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
        doc.addFileToVFS('Roboto-Bold.ttf', boldBase64);
        doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
        return true;
    }
    catch {
        return false;
    }
}
function getImageDimensions(base64) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = () => resolve({ width: 1, height: 1 });
        img.src = base64;
    });
}
function formatDate(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pl-PL');
}
// Colors
const PRIMARY_COLOR = [200, 30, 30];
const DARK_TEXT = [33, 33, 33];
const GRAY_TEXT = [120, 120, 120];
const LIGHT_BG = [245, 245, 245];
const BORDER_COLOR = [220, 220, 220];
function drawRoundedRect(doc, x, y, w, h, r, fill, stroke) {
    if (fill)
        doc.setFillColor(...fill);
    if (stroke) {
        doc.setDrawColor(...stroke);
        doc.setLineWidth(0.3);
    }
    doc.roundedRect(x, y, w, h, r, r, fill && stroke ? 'FD' : fill ? 'F' : 'S');
}
export async function generateObjectPdf(data) {
    const { translations } = data;
    const obj = data.object;
    const apiKey = process.env.VUE_APP_GOOGLE_API_KEY;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    const fontsLoaded = await registerFonts(doc);
    if (fontsLoaded) {
        doc.setFont('Roboto');
    }
    let y = margin;
    // --- Load images in parallel ---
    const hasCoordinates = obj.lat != null && obj.lng != null;
    const mapUrl = hasCoordinates && apiKey
        ? buildStaticMapUrl(obj.lat, obj.lng, apiKey)
        : null;
    const [objektoLogoBase64, mainPhotoBase64, mapBase64] = await Promise.all([
        loadImageAsBase64('/android-chrome-192x192.png'),
        obj.logoId
            ? fetchFileViaApi(obj.logoId)
            : obj.logoUrl
                ? loadImageAsBase64(obj.logoUrl)
                : null,
        mapUrl ? loadImageAsBase64(mapUrl) : null,
    ]);
    // ============================================================
    // HEADER — Objekto logo + name left, type + price right
    // ============================================================
    const headerHeight = 18;
    let logoTextX = margin;
    if (objektoLogoBase64) {
        try {
            const logoH = 12;
            const logoW = 12;
            doc.addImage(objektoLogoBase64, 'PNG', margin, y + (headerHeight - logoH) / 2, logoW, logoH);
            logoTextX = margin + logoW + 3;
        }
        catch {
            // fallback — just text
        }
    }
    doc.setFontSize(11);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...DARK_TEXT);
    doc.text('objek.to', logoTextX, y + 11);
    // Object type name right-aligned
    const objectTypeName = obj.objectType?.name || '';
    doc.setFontSize(13);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...DARK_TEXT);
    doc.text(objectTypeName, pageWidth - margin, y + 8, {
        align: 'right',
    });
    // Price below type name
    if (obj.effectiveSaleMonthlyPrice !== null &&
        obj.effectiveSaleMonthlyPrice !== undefined) {
        doc.setFontSize(9);
        doc.setFont('Roboto', 'normal');
        doc.setTextColor(...GRAY_TEXT);
        doc.text(`${translations.exposure}: ${obj.effectiveSaleMonthlyPrice} ${translations.currency}`, pageWidth - margin, y + 14, { align: 'right' });
    }
    y += headerHeight + 6;
    // ============================================================
    // MAIN PHOTO
    // ============================================================
    if (mainPhotoBase64) {
        try {
            const photoDims = await getImageDimensions(mainPhotoBase64);
            const maxPhotoHeight = 85;
            const photoRatio = photoDims.width / photoDims.height;
            let photoWidth = contentWidth;
            let photoHeight = photoWidth / photoRatio;
            if (photoHeight > maxPhotoHeight) {
                photoHeight = maxPhotoHeight;
                photoWidth = photoHeight * photoRatio;
            }
            // Gray background behind photo area
            doc.setFillColor(...LIGHT_BG);
            doc.rect(margin, y, contentWidth, maxPhotoHeight, 'F');
            const photoX = margin + (contentWidth - photoWidth) / 2;
            const photoY = y + (maxPhotoHeight - photoHeight) / 2;
            doc.addImage(mainPhotoBase64, 'JPEG', photoX, photoY, photoWidth, photoHeight);
            y += maxPhotoHeight + 6;
        }
        catch {
            // skip photo on error
        }
    }
    // ============================================================
    // OBJECT TITLE + ADDRESS + DATES
    // ============================================================
    doc.setFontSize(16);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text(`${translations.objectLabel} ${obj.internalNumber || ''}`, margin, y + 5, { maxWidth: contentWidth });
    y += 10;
    doc.setFontSize(10);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(...DARK_TEXT);
    const address = getFullAddress({
        address: obj.address || '',
        postalCode: obj.postalCode || '',
        city: obj.city || '',
    });
    const addressLines = doc.splitTextToSize(address, contentWidth);
    doc.text(addressLines, margin, y);
    y += addressLines.length * 5;
    // Object type name
    const objectTypeLabel = obj.objectType?.name || '';
    if (objectTypeLabel) {
        doc.setFontSize(10);
        doc.setFont('Roboto', 'normal');
        doc.setTextColor(...GRAY_TEXT);
        doc.text(objectTypeLabel, margin, y);
        y += 5;
    }
    doc.setFontSize(8);
    doc.setTextColor(...GRAY_TEXT);
    let dateText = '';
    if (obj.createdAt)
        dateText += `${translations.created}: ${formatDate(obj.createdAt)}`;
    if (obj.updatedAt)
        dateText += `    ${translations.modified}: ${formatDate(obj.updatedAt)}`;
    if (dateText) {
        doc.text(dateText, margin, y);
        y += 5;
    }
    y += 4;
    // ============================================================
    // TWO-COLUMN SECTION: DETAILS (left) + MAP (right)
    // ============================================================
    const hasMap = !!mapBase64;
    const colGap = 5;
    const leftColWidth = hasMap
        ? Math.floor((contentWidth - colGap) * 0.52)
        : contentWidth;
    const rightColWidth = hasMap ? contentWidth - leftColWidth - colGap : 0;
    const details = [
        [translations.placeNumber, obj.place?.number],
        [
            translations.coordinates,
            hasCoordinates ? `${obj.lat}, ${obj.lng}` : null,
        ],
        [
            translations.dimensions,
            getObjectDimensions(obj.width, obj.height, obj.isInCentimeters),
        ],
        [
            translations.exposure,
            obj.effectiveSaleMonthlyPrice != null
                ? `${obj.effectiveSaleMonthlyPrice} ${translations.currency}`
                : null,
        ],
        [translations.videoLink, obj.videoUrl],
        [translations.light, obj.isLit ? translations.yes : translations.no],
        [translations.format, obj.format],
        [translations.material, obj.material],
    ];
    const detailRowHeight = 6;
    const detailsCardHeight = 10 + details.length * detailRowHeight + 4;
    const sectionStartY = y;
    // --- LEFT COLUMN: Details card ---
    drawRoundedRect(doc, margin, y, leftColWidth, detailsCardHeight, 3, LIGHT_BG, BORDER_COLOR);
    y += 7;
    doc.setFontSize(11);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...DARK_TEXT);
    doc.text(translations.details, margin + 5, y);
    y += 6;
    doc.setFontSize(9);
    const labelX = margin + 5;
    const valueX = hasMap ? margin + 48 : margin + 58;
    const maxValueWidth = hasMap ? leftColWidth - 53 : contentWidth - 63 - 5;
    for (const [label, value] of details) {
        doc.setFont('Roboto', 'bold');
        doc.setTextColor(...GRAY_TEXT);
        doc.text(`${label}:`, labelX, y);
        doc.setFont('Roboto', 'normal');
        doc.setTextColor(...DARK_TEXT);
        const displayValue = String(value ?? '-');
        const truncatedValue = doc.getTextWidth(displayValue) > maxValueWidth
            ? displayValue.substring(0, hasMap ? 30 : 55) + '...'
            : displayValue;
        doc.text(truncatedValue, valueX, y);
        y += detailRowHeight;
    }
    // --- RIGHT COLUMN: Map ---
    if (hasMap && mapBase64) {
        const mapX = margin + leftColWidth + colGap;
        drawRoundedRect(doc, mapX, sectionStartY, rightColWidth, detailsCardHeight, 3, LIGHT_BG, BORDER_COLOR);
        // Localization title
        doc.setFontSize(11);
        doc.setFont('Roboto', 'bold');
        doc.setTextColor(...DARK_TEXT);
        doc.text(translations.localization, mapX + 5, sectionStartY + 7);
        // Map image inside the card
        const mapImgPad = 4;
        const mapImgY = sectionStartY + 12;
        const mapImgH = detailsCardHeight - 16;
        if (mapImgH > 18) {
            const addressLineH = 6;
            try {
                doc.addImage(mapBase64, 'PNG', mapX + mapImgPad, mapImgY, rightColWidth - 2 * mapImgPad, mapImgH - addressLineH);
                // Address under map
                doc.setFontSize(8);
                doc.setFont('Roboto', 'normal');
                doc.setTextColor(...GRAY_TEXT);
                doc.text(address, mapX + mapImgPad, mapImgY + mapImgH - addressLineH + 4, {
                    maxWidth: rightColWidth - 2 * mapImgPad,
                });
            }
            catch {
                // skip map on error
            }
        }
    }
    // ============================================================
    // FOOTER
    // ============================================================
    doc.setFontSize(7);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(...GRAY_TEXT);
    doc.text(`objek.to  |  ${new Date().toLocaleDateString('pl-PL')}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    // --- Save ---
    const fileName = `object-${obj.internalNumber || obj.id}.pdf`;
    doc.save(fileName);
}
